/**
 * oauthConfig.js
 * Frontend-side OAuth provider metadata.
 *
 * Client IDs are PUBLIC and come from build-time env vars (REACT_APP_*).
 * Client SECRETS never live here — they stay on the server (.env on EC2).
 *
 * Styles:
 *   'oidc-google' : Google Identity Services popup -> ID token (credential)
 *   'oidc-redirect': OIDC provider via redirect -> id_token in callback
 *   'code'        : OAuth2 authorization-code redirect -> code in callback
 *
 * A provider is only shown if its clientId is set AND the backend reports it as
 * configured (GET /auth/providers).
 */

const origin = typeof window !== 'undefined' ? window.location.origin : '';
const redirectUri = `${origin}/auth/callback`;

export const OAUTH_REDIRECT_URI = redirectUri;

export const PROVIDERS = {
  google: {
    label: 'Google',
    style: 'oidc-google',
    clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    brandClass: 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
  },
  microsoft: {
    label: 'Microsoft',
    style: 'oidc-redirect',
    clientId: process.env.REACT_APP_MICROSOFT_CLIENT_ID,
    authorizeUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
    scope: 'openid email profile',
    responseType: 'id_token',
    extraParams: { response_mode: 'fragment' },
    brandClass: 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
  },
  facebook: {
    label: 'Facebook',
    // Facebook web login uses the OAuth authorization-CODE flow. It does NOT
    // issue an OIDC id_token via the web dialog and REJECTS the "openid"/"email"
    // OIDC scope set ("Invalid Scopes: email"). We request Graph permissions and
    // exchange the returned code server-side for an access token + /me profile.
    style: 'code',
    clientId: process.env.REACT_APP_FACEBOOK_CLIENT_ID,
    authorizeUrl: 'https://www.facebook.com/v18.0/dialog/oauth',
    scope: 'email public_profile',
    responseType: 'code',
    extraParams: {},
    brandClass: 'border-[#1877F2] bg-[#1877F2] text-white hover:bg-[#166fe0]',
  },
  apple: {
    // Sign in with Apple (web). clientId is the Apple "Services ID".
    // Uses response_mode=fragment so the id_token lands in the URL hash and the
    // existing GET /auth/callback handler can read it (same as Facebook/Microsoft).
    // We request only `email` (not `name`); requesting `name` would force Apple's
    // form_post mode (a POST callback), which this SPA callback route can't handle.
    style: 'oidc-redirect',
    clientId: process.env.REACT_APP_APPLE_CLIENT_ID,
    authorizeUrl: 'https://appleid.apple.com/auth/authorize',
    scope: 'openid email',
    responseType: 'id_token',
    extraParams: { response_mode: 'fragment' },
    brandClass: 'border-black bg-black text-white hover:bg-gray-900',
  },
  amazon: {
    label: 'Amazon',
    style: 'code',
    clientId: process.env.REACT_APP_AMAZON_CLIENT_ID,
    authorizeUrl: 'https://www.amazon.com/ap/oa',
    scope: 'profile',
    responseType: 'code',
    extraParams: {},
    brandClass: 'border-[#FF9900] bg-[#FF9900] text-white hover:bg-[#e88a00]',
  },
  github: {
    label: 'GitHub',
    style: 'code',
    clientId: process.env.REACT_APP_GITHUB_CLIENT_ID,
    authorizeUrl: 'https://github.com/login/oauth/authorize',
    scope: 'read:user user:email',
    responseType: 'code',
    extraParams: {},
    brandClass: 'border-gray-800 bg-gray-800 text-white hover:bg-gray-900',
  },
  linkedin: {
    label: 'LinkedIn',
    style: 'code',
    clientId: process.env.REACT_APP_LINKEDIN_CLIENT_ID,
    authorizeUrl: 'https://www.linkedin.com/oauth/v2/authorization',
    scope: 'openid profile email',
    responseType: 'code',
    extraParams: {},
    brandClass: 'border-[#0A66C2] bg-[#0A66C2] text-white hover:bg-[#095196]',
  },
  twitter: {
    label: 'X',
    style: 'code',
    clientId: process.env.REACT_APP_TWITTER_CLIENT_ID,
    authorizeUrl: 'https://twitter.com/i/oauth2/authorize',
    scope: 'tweet.read users.read',
    responseType: 'code',
    // X requires PKCE. We use a fixed challenge/verifier pair for the plain method.
    extraParams: { code_challenge: 'challenge', code_challenge_method: 'plain' },
    codeVerifier: 'challenge',
    brandClass: 'border-black bg-black text-white hover:bg-gray-900',
  },
};
